import React, { useState, useEffect } from 'react';
import { adminAPI, useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useModal } from '@/contexts/ModalContext';
import { toast } from '@/components/ui/sonner';
import { 
  MessageSquare, 
  Search, 
  Filter, 
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Calendar,
  Send,
  Eye,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

interface SupportRequest {
  _id: string;
  userId: string;
  userEmail: string;
  userName: string;
  subject: string;
  message: string;
  category: string;
  priority: string;
  status: string;
  assignedTo?: {
    adminId: string;
    adminEmail: string;
    assignedAt: string;
  };
  responses: Array<{
    responderId: string;
    responderEmail: string;
    responderName: string;
    message: string;
    isAdminResponse: boolean;
    createdAt: string;
  }>;
  createdAt: string;
  resolvedAt?: string;
  closedAt?: string;
}

interface SupportResponse {
  requests: SupportRequest[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRequests: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const AdminSupportPage: React.FC = () => {
  const { user } = useAdminAuth();
  const { showConfirmModal } = useModal();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    assignedTo: '',
    search: '',
    page: 1,
    limit: 20
  });

  useEffect(() => {
    loadRequests();
  }, [filters]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getSupportRequests(filters);
      setRequests(response.data.requests);
      setPagination(response.data.pagination);
    } catch (error: any) {
      setError(error.message || 'Failed to load support requests');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleAssignRequest = async (requestId: string) => {
    if (!user) return;
    
    try {
      setActionLoading(`assign-${requestId}`);
      await adminAPI.assignSupportRequest(requestId, user.uid);
      await loadRequests();
      if (selectedRequest?._id === requestId) {
        const updatedRequest = await adminAPI.getSupportRequestDetails(requestId);
        setSelectedRequest(updatedRequest.data.request);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to assign request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddResponse = async () => {
    if (!selectedRequest || !responseMessage.trim()) return;
    
    try {
      setActionLoading('response');
      await adminAPI.addSupportResponse(selectedRequest._id, responseMessage);
      setResponseMessage('');
      
      // Reload the selected request
      const updatedRequest = await adminAPI.getSupportRequestDetails(selectedRequest._id);
      setSelectedRequest(updatedRequest.data.request);
      await loadRequests();
    } catch (error: any) {
      setError(error.message || 'Failed to add response');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusChange = async (requestId: string, status: string) => {
    // Get confirmation for status changes
    const statusMessages = {
      'in_progress': 'Mark this support request as In Progress?',
      'resolved': 'Mark this support request as Resolved?',
      'closed': 'Close this support request?',
      'open': 'Reopen this support request?'
    };

    const confirmed = await showConfirmModal({
      title: 'Update Support Request Status',
      description: statusMessages[status as keyof typeof statusMessages] || 'Update the status of this support request?',
      variant: status === 'resolved' ? 'default' : status === 'closed' ? 'warning' : 'default',
      confirmText: 'Update Status',
      cancelText: 'Cancel'
    });

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(`status-${requestId}`);
      await adminAPI.updateSupportRequestStatus(requestId, status);
      await loadRequests();
      if (selectedRequest?._id === requestId) {
        const updatedRequest = await adminAPI.getSupportRequestDetails(requestId);
        setSelectedRequest(updatedRequest.data.request);
      }

      // Show success toast
      const statusLabels = {
        'in_progress': 'In Progress',
        'resolved': 'Resolved',
        'closed': 'Closed',
        'open': 'Reopened'
      };
      toast.success(`Support request marked as ${statusLabels[status as keyof typeof statusLabels] || status}`);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update status';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'open': { color: 'bg-red-100 text-red-800', icon: AlertCircle },
      'in_progress': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'resolved': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'closed': { color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityColors = {
      'low': 'bg-blue-100 text-blue-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge className={priorityColors[priority as keyof typeof priorityColors] || priorityColors.medium}>
        {priority.toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && requests.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Support Management</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            Support Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage user support requests and provide assistance
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadRequests}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Requests List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Search
                  </label>
                  <Input
                    placeholder="Subject, email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Categories</option>
                    <option value="technical">Technical</option>
                    <option value="billing">Billing</option>
                    <option value="feature_request">Feature Request</option>
                    <option value="bug_report">Bug Report</option>
                    <option value="general">General</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Priority
                  </label>
                  <select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requests */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Support Requests ({pagination?.totalRequests || 0})</span>
                <div className="text-sm text-gray-500">
                  Page {pagination?.currentPage || 1} of {pagination?.totalPages || 1}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {requests.map((request) => (
                  <div 
                    key={request._id} 
                    className={`border border-border rounded-lg p-4 cursor-pointer hover:bg-accent/5 bg-card ${
                      selectedRequest?._id === request._id ? 'ring-2 ring-primary bg-accent/10' : ''
                    }`}
                    onClick={() => setSelectedRequest(request)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{request.subject}</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <User className="h-4 w-4" />
                          <span>{request.userEmail}</span>
                          <Calendar className="h-4 w-4 ml-2" />
                          <span>{formatDate(request.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        {getStatusBadge(request.status)}
                        {getPriorityBadge(request.priority)}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {request.message}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="capitalize">{request.category.replace('_', ' ')}</span>
                      <div className="flex items-center space-x-2">
                        {request.assignedTo && (
                          <span>Assigned to: {request.assignedTo.adminEmail}</span>
                        )}
                        <span>{request.responses.length} responses</span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {requests.length === 0 && !loading && (
                  <div className="text-center py-8 text-gray-500">
                    No support requests found matching your criteria
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
                {Math.min(pagination.currentPage * filters.limit, pagination.totalRequests)} of{' '}
                {pagination.totalRequests} requests
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Request Details */}
        <div className="space-y-4">
          {selectedRequest ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Request Details</span>
                    <div className="flex items-center space-x-2">
                      {!selectedRequest.assignedTo && (
                        <Button
                          size="sm"
                          onClick={() => handleAssignRequest(selectedRequest._id)}
                          disabled={actionLoading === `assign-${selectedRequest._id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Assign to Me
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">{selectedRequest.subject}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                      <span>From: {selectedRequest.userEmail}</span>
                      <span>Created: {formatDate(selectedRequest.createdAt)}</span>
                    </div>
                    <div className="flex items-center space-x-2 mb-3">
                      {getStatusBadge(selectedRequest.status)}
                      {getPriorityBadge(selectedRequest.priority)}
                      <Badge className="bg-gray-100 text-gray-800 capitalize">
                        {selectedRequest.category.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-2">Original Message</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {selectedRequest.message}
                    </p>
                  </div>
                  
                  {selectedRequest.assignedTo && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-900 mb-1">Assigned To</h4>
                      <p className="text-sm text-gray-600">
                        {selectedRequest.assignedTo.adminEmail} on{' '}
                        {formatDate(selectedRequest.assignedTo.assignedAt)}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedRequest.status}
                      onChange={(e) => handleStatusChange(selectedRequest._id, e.target.value)}
                      disabled={actionLoading === `status-${selectedRequest._id}`}
                      className="px-3 py-1 border border-border rounded text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* Responses */}
              <Card>
                <CardHeader>
                  <CardTitle>Responses ({selectedRequest.responses.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedRequest.responses.map((response, index) => (
                      <div 
                        key={index} 
                        className={`p-3 rounded-lg ${
                          response.isAdminResponse 
                            ? 'bg-blue-50 border-l-4 border-blue-500' 
                            : 'bg-gray-50 border-l-4 border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">
                            {response.responderName || response.responderEmail}
                          </span>
                          <div className="flex items-center space-x-2">
                            {response.isAdminResponse && (
                              <Badge className="bg-blue-100 text-blue-800 text-xs">Admin</Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {formatDate(response.createdAt)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {response.message}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t pt-4">
                    <Textarea
                      placeholder="Type your response..."
                      value={responseMessage}
                      onChange={(e) => setResponseMessage(e.target.value)}
                      rows={4}
                      className="mb-3"
                    />
                    <Button
                      onClick={handleAddResponse}
                      disabled={!responseMessage.trim() || actionLoading === 'response'}
                      className="w-full"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send Response
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Select a support request to view details and respond</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportPage;
